import { makeObservable, observable, runInAction, action } from "mobx";
import { ApiSchema, api } from "@/modules/api";
import { WithAppStore } from "@/store/types";
import { AppStore } from "@/store";
import { PerDeviceOnboardingInfoObservable } from "@/store/platform-info/PerDeviceOnboardingInfoObservable";
import { BaseError } from "@/domains/errors";
import { objectModule } from "@/modules/object";
import pRetry from "p-retry";

export class PlatformOnboardingInfoObservable {
  store: AppStore;
  perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
  shouldShowIcpRfi: boolean;
  shouldShowHeadsUpCoachMark: boolean;
  rfiCompletedSteps: string[];

  constructor({
    perDeviceOnboardingInfo,
    shouldShowIcpRfi,
    shouldShowHeadsUpCoachMark,
    rfiCompletedSteps = [],
    store,
  }: {
    perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
    shouldShowIcpRfi?: boolean;
    shouldShowHeadsUpCoachMark?: boolean;
    rfiCompletedSteps?: string[];
  } & WithAppStore) {
    this.store = store;
    this.perDeviceOnboardingInfo = perDeviceOnboardingInfo;
    this.shouldShowIcpRfi = shouldShowIcpRfi ?? false;
    this.shouldShowHeadsUpCoachMark = shouldShowHeadsUpCoachMark ?? false;
    this.rfiCompletedSteps = rfiCompletedSteps;

    makeObservable(this, {
      store: false,
      perDeviceOnboardingInfo: false,
      shouldShowIcpRfi: observable,
      shouldShowHeadsUpCoachMark: observable,
      rfiCompletedSteps: observable,
      markStepCompleted: action,
      completeIcpRfiStep: action,
      debugReset: action,
    });
  }

  markStepCompleted(stepId: string) {
    runInAction(() => {
      if (!this.rfiCompletedSteps.includes(stepId)) {
        this.rfiCompletedSteps.push(stepId);
      }
    });
  }

  async completeIcpRfiStep(completedSteps: string[] = []) {
    for (const step of completedSteps) {
      this.markStepCompleted(step);
    }
    if (completedSteps.includes("referrer")) {
      return this.store.platformInfo.dismissIcpRfi({ completedSteps });
    }
  }

  async debugReset() {
    runInAction(() => {
      this.shouldShowIcpRfi = true;
      this.rfiCompletedSteps = [];
    });

    await pRetry(async () => {
      const response = await api.post("/v2/me/platform-info/reset-icp-rfi");

      if (response?.data?.onboarding_info) {
        const apiOnboardingInfo = response.data
          .onboarding_info as ApiSchema["PlatformInfoOnboarding"];

        const updated = PlatformOnboardingInfoObservable.createFromRemoteData({
          data: apiOnboardingInfo,
          store: this.store,
          perDeviceOnboardingInfo: this.perDeviceOnboardingInfo,
        });

        runInAction(() => {
          this.shouldShowIcpRfi = updated.shouldShowIcpRfi;
          this.shouldShowHeadsUpCoachMark = updated.shouldShowHeadsUpCoachMark;
          this.rfiCompletedSteps = updated.rfiCompletedSteps;
        });
        return;
      }

      if (response?.error) {
        const err = new BaseError({
          message: "Failed to reset ICP RFI for current account.",
          info: {
            response: objectModule.safeAsJson(response),
          },
        });
        throw err;
      }
    });
  }

  public static createFromRemoteData({
    data,
    store,
    perDeviceOnboardingInfo,
  }: {
    data: ApiSchema["PlatformInfoOnboarding"];
    perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
  } & WithAppStore) {
    // Type-safe property access with fallbacks for new RFI properties
    type ExtendedOnboardingData = ApiSchema["PlatformInfoOnboarding"] & {
      should_show_rfi?: boolean;
      should_show_icp_rfi?: boolean;
      should_show_heads_up_coach_mark?: boolean;
      rfi_completed_steps?: string[];
    };
    const extendedData = data as ExtendedOnboardingData;

    return new PlatformOnboardingInfoObservable({
      shouldShowIcpRfi: extendedData.should_show_icp_rfi ?? extendedData.should_show_rfi ?? false,
      shouldShowHeadsUpCoachMark: extendedData.should_show_heads_up_coach_mark ?? false,
      rfiCompletedSteps: extendedData.rfi_completed_steps ?? [],
      store,
      perDeviceOnboardingInfo,
    });
  }
}
